import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Draft, IntensityKind } from "@jackedlogs/shared";

import type { useTimelineLogger } from "../../Logger/useTimelineLogger";
import {
  applyPrescribeAxis,
  applyPrescribeEntryNote,
  applyPrescribeNote,
  applyPrescribeWorkKind,
  resolvePrescribeAddr,
  type PrescribeAddrCtx,
} from "./prescribeDispatch";

/** PE-22 K3.2/K3.3/K3.4 — the bound set's prescription commands beyond reps
 *  and effort (which usePrescribeMount keeps, next to `dispatch` and
 *  `effort`): the load-axis selector and the "A tempo"/"Nota" chips. ⛔ K3.3's
 *  "apply to all sets" is GONE — PE-31, applying the locked design of 28/08
 *  («"Applica a tutti" eliminato, guadagna una riga»), confirmed by Antenore
 *  29/08. Split out of usePrescribeMount.ts (K3.4 landed it
 *  exactly at the 500-line cap) — a pure extraction, same addressing
 *  discipline (resolved from the live draft at write time, never a captured
 *  index) as every other prescribeDispatch caller. */
export function usePrescribeSetCommands(
  setDraft: Dispatch<SetStateAction<Draft>>,
  addrCtx: PrescribeAddrCtx,
  tl: ReturnType<typeof useTimelineLogger>,
) {
  // K3.2 — the LoadAxisSelector's write: same addressing as onPrescribeEffort
  // (resolved to the program-Draft address at tap time, never a captured
  // index). null while no set is bound — the selector itself is not
  // rendered then (see `loadAxis` below).
  const onPrescribeAxis = useCallback(
    (kind: IntensityKind) =>
      setDraft((d) =>
        applyPrescribeAxis(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
          kind,
        ),
      ),
    [setDraft, addrCtx, tl.activeEntryIdx, tl.boundSetIdx],
  );
  const loadAxis =
    tl.boundSet === null
      ? null
      : {
          kind: tl.boundSet.intensityPrescribed?.kind ?? ("none" as const),
          onSelect: onPrescribeAxis,
        };

  // K3.4 — "A tempo": the reps⇄time toggle. Seeds from the exercise's own
  // default when turning on, exactly like the live logger's start-timer
  // affordance seeds a countdown (buildTimerStartHandlers) — never a bare
  // "30" when the catalog already names this exercise's bout length.
  const onToggleTimed = useCallback(() => {
    const timedNow = tl.boundSet?.workPrescribed?.kind === "countdown";
    const seed = String(tl.activeExercise?.defaultDurationSec ?? 30);
    setDraft((d) =>
      applyPrescribeWorkKind(
        d,
        resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
        !timedNow,
        seed,
      ),
    );
  }, [
    setDraft,
    addrCtx,
    tl.activeEntryIdx,
    tl.boundSetIdx,
    tl.boundSet,
    tl.activeExercise,
  ]);

  // K3.4 — "Nota": writes the row's per-week note as the coach types.
  const onChangeNote = useCallback(
    (text: string) =>
      setDraft((d) =>
        applyPrescribeNote(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
          text,
        ),
      ),
    [setDraft, addrCtx, tl.activeEntryIdx, tl.boundSetIdx],
  );

  // PE-37 — the per-EXERCISE note. ⭐ Resolves the address at set 0 and uses
  // only its slot half: the note is the exercise's, so it does not depend on
  // which set is bound — and that is exactly why this chip survives when no
  // set is, unlike the per-set one below.
  const onChangeEntryNote = useCallback(
    (text: string) =>
      setDraft((d) =>
        applyPrescribeEntryNote(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, 0),
          text,
        ),
      ),
    [setDraft, addrCtx, tl.activeEntryIdx],
  );

  return {
    loadAxis,
    entryNote:
      tl.activeEntryIdx < 0
        ? undefined
        : {
            text: tl.activeEntry?.notePrescribed ?? "",
            onChange: onChangeEntryNote,
          },
    // undefined while no set is bound — FocusPanelChipRow reads the
    // callback's presence as the chip's visibility, same convention
    // `onSplitGroup`/`onCreateGroup` already use on FocusPanelProps.
    timedChip:
      tl.boundSet === null
        ? undefined
        : {
            active: tl.boundSet.workPrescribed?.kind === "countdown",
            onToggle: onToggleTimed,
          },
    noteChip:
      tl.boundSet === null
        ? undefined
        : {
            active: (tl.boundSet.notePrescribed ?? "").trim() !== "",
            text: tl.boundSet.notePrescribed ?? "",
            onChange: onChangeNote,
          },
  };
}
